// Reservas simuladas de espacios (demo): viven en el navegador (localStorage)
// y las crea el avatar por voz o cualquier otra parte de la app. Reglas por
// tipo de reserva de la zona (aforoConfig, campo `reservable`):
//  - "sala":   la zona entera (salas de reuniones, despachos); no puede
//              solaparse con otra reserva.
//  - "puesto": N plazas individuales (puestos de trabajo, plazas de parking);
//              la suma de reservas solapadas no puede pasar del aforo. En las
//              zonas con mesas (aforoBloques) cada reserva va a UNA mesa
//              ("Mesa 3"), la pedida o la primera con sitio.
//  - "turno":  franjas de media hora con el aforo como capacidad (comedores).
// Una reserva cubre un tramo horario de uno o varios días seguidos (de
// `fecha` a `fechaFin`, ambos incluidos).
// Relación con la ocupación, en UN SOLO SENTIDO:
//  - Las reservas SÍ cuentan en la ocupación (una sala reservada tendrá gente:
//    el simulador la da por llena en su tramo y suma los puestos o plazas).
//  - La ocupación NO cuenta para reservar: la disponibilidad depende solo de
//    lo ya reservado (que se prevea poca gente no significa que esté libre).
// Las reservas tienen además su propia vista en el módulo de aforo.
import { AFORO_ZONES, AforoReservable, AforoZone } from "./aforoConfig";
import { AforoBloque, aforoBloques } from "./aforoBloques";

export interface AforoReserva {
    id: string;
    zoneId: string;
    tipo: AforoReservable;
    /** Primer día, AAAA-MM-DD. */
    fecha: string;
    /** Último día incluido, AAAA-MM-DD (igual a `fecha` si es de un día). */
    fechaFin: string;
    /** Horas decimales (9.5 = 09:30), cada día del tramo. */
    inicio: number;
    fin: number;
    nombre: string;
    /** Puestos o plazas reservados (1 para salas). */
    cantidad: number;
    /** Mesa a la que van los puestos (zonas con bloques). */
    bloqueId?: string;
    bloqueNombre?: string;
    creadaEn: string;
}

const STORAGE_KEY = "aforo.reservas.v1";
const TURNO_HOURS = 0.5;
// Tope de días seguidos por reserva (evita bucles con fechas absurdas).
const MAX_DIAS = 62;

export const normaliza = (value: string): string =>
    (value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

type Listener = () => void;

const listeners = new Set<Listener>();

const load = (): AforoReserva[] => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as AforoReserva[];
        if (!Array.isArray(parsed)) return [];
        // Reservas guardadas antes de los tramos de varios días.
        return parsed.map((r) => ({ ...r, fechaFin: r.fechaFin || r.fecha }));
    } catch (err) {
        return [];
    }
};

let reservas: AforoReserva[] = load();
// Última reserva hecha por voz: su chip del plano sale desplegado.
let destacada: string | null = null;

const save = (): void => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(reservas));
    } catch (err) {
        // sin localStorage (modo privado): las reservas duran lo que la página
    }
    listeners.forEach((l) => l());
};

const zoneById = (zoneId: string): AforoZone | undefined =>
    AFORO_ZONES.find((z) => z.id === zoneId);

/** La reserva cubre ese día y se solapa con el tramo horario. */
const overlaps = (r: AforoReserva, fecha: string, inicio: number, fin: number): boolean =>
    r.fecha <= fecha && fecha <= r.fechaFin && r.inicio < fin && r.fin > inicio;

export const formatHora = (hour: number): string => {
    const hh = Math.floor(hour) % 24;
    const mm = Math.round((hour % 1) * 60);
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

/** "10:30" o "10" → 10.5 / 10; null si no es una hora. */
export const parseHora = (value: string): number | null => {
    const m = /^\s*(\d{1,2})(?:[:.h](\d{2}))?\s*$/.exec(value || "");
    if (!m) return null;
    const h = Number(m[1]);
    const min = Number(m[2] || 0);
    if (h < 0 || h > 24 || min < 0 || min > 59) return null;
    return h + min / 60;
};

export const esFecha = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test((value || "").trim());

/** Día siguiente (AAAA-MM-DD), sin líos de zona horaria. */
export const diaSiguiente = (dateKey: string): string => {
    const d = new Date(`${dateKey}T12:00:00`);
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
};

/** Días de un tramo [desde, hasta], ambos incluidos (acotado). */
export const diasEntre = (desde: string, hasta: string): string[] => {
    const dias: string[] = [];
    let d = desde;
    while (d <= hasta && dias.length < MAX_DIAS) {
        dias.push(d);
        d = diaSiguiente(d);
    }
    return dias;
};

export interface DisponibilidadBloque {
    bloque: AforoBloque;
    capacidad: number;
    reservado: number;
    libre: number;
    reservas: AforoReserva[];
}

export interface Disponibilidad {
    zone: AforoZone;
    tipo: AforoReservable;
    /** Capacidad total reservable (1 para salas). */
    capacidad: number;
    /** Lo ya reservado en ese tramo (máximo simultáneo). */
    reservado: number;
    libre: number;
    /** Reservas que se solapan con el tramo. */
    reservas: AforoReserva[];
    /** Detalle por mesa (zonas con bloques); vacío en el resto. */
    bloques: DisponibilidadBloque[];
}

const unidades = (zone: AforoZone, r: AforoReserva): number =>
    zone.reservable === "sala" ? 1 : r.cantidad;

/**
 * Reparto de las reservas solapadas entre las mesas de una zona en un instante:
 * primero las que nombran mesa, luego las sueltas (de antes de las mesas)
 * rellenando por orden. Devuelve lo reservado por mesa.
 */
const repartoPorBloque = (
    bloques: AforoBloque[],
    activas: AforoReserva[]
): { [bloqueId: string]: number } => {
    const porBloque: { [bloqueId: string]: number } = {};
    bloques.forEach((b) => {
        porBloque[b.id] = 0;
    });
    activas
        .filter((r) => r.bloqueId && porBloque[r.bloqueId] !== undefined)
        .forEach((r) => {
            porBloque[r.bloqueId!] += r.cantidad;
        });
    activas
        .filter((r) => !r.bloqueId || porBloque[r.bloqueId] === undefined)
        .forEach((r) => {
            let pendiente = r.cantidad;
            bloques.forEach((b) => {
                if (pendiente <= 0) return;
                const hueco = Math.max(0, b.puestos - porBloque[b.id]);
                const toma = Math.min(hueco, pendiente);
                porBloque[b.id] += toma;
                pendiente -= toma;
            });
        });
    return porBloque;
};

export const aforoReservations = {
    onChange(listener: Listener): () => void {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },

    todas(): AforoReserva[] {
        return [...reservas].sort((a, b) =>
            a.fecha === b.fecha ? a.inicio - b.inicio : a.fecha.localeCompare(b.fecha)
        );
    },

    /** Reservas que cubren un día. */
    porFecha(fecha: string, zoneIds?: Set<string>): AforoReserva[] {
        return this.todas().filter(
            (r) => r.fecha <= fecha && fecha <= r.fechaFin && (!zoneIds || zoneIds.has(r.zoneId))
        );
    },

    /** Reservas de una persona (búsqueda aproximada por nombre). */
    porNombre(nombre: string): AforoReserva[] {
        const n = normaliza(nombre);
        if (!n) return [];
        return this.todas().filter((r) => {
            const rn = normaliza(r.nombre);
            return rn === n || rn.includes(n) || n.includes(rn);
        });
    },

    /** Personas que suponen las reservas en una zona a una hora (para la ocupación). */
    ocupacionReservada(zoneId: string, fecha: string, hora: number): number {
        const zone = zoneById(zoneId);
        if (!zone || !zone.reservable) return 0;
        const activas = reservas.filter(
            (r) => r.zoneId === zoneId && overlaps(r, fecha, hora, hora + 0.001)
        );
        if (activas.length === 0) return 0;
        // Sala o despacho reservados enteros: cuentan los asistentes que se
        // dijeron al reservar (sin dato, la sala llena).
        if (zone.reservable === "sala") {
            return Math.min(zone.aforoMaximo, activas[0].cantidad || zone.aforoMaximo);
        }
        return Math.min(
            zone.aforoMaximo,
            activas.reduce((acc, r) => acc + r.cantidad, 0)
        );
    },

    disponibilidad(
        zoneId: string,
        fecha: string,
        inicio: number,
        fin: number
    ): Disponibilidad | null {
        const zone = zoneById(zoneId);
        if (!zone || !zone.reservable) return null;
        const solapadas = reservas.filter(
            (r) => r.zoneId === zoneId && overlaps(r, fecha, inicio, fin)
        );
        const capacidad = zone.reservable === "sala" ? 1 : zone.aforoMaximo;
        const bloques = zone.reservable === "puesto" ? aforoBloques.bloquesDe(zoneId) : [];
        // Máximo simultáneo dentro del tramo (las reservas pueden solaparse
        // entre sí solo parcialmente): se evalúa en cada borde de reserva.
        const cortes = new Set<number>([inicio]);
        solapadas.forEach((r) => {
            if (r.inicio > inicio && r.inicio < fin) cortes.add(r.inicio);
        });
        let reservado = 0;
        const maxPorBloque: { [bloqueId: string]: number } = {};
        cortes.forEach((t) => {
            const activas = solapadas.filter((r) => r.inicio <= t && r.fin > t);
            const enT = activas.reduce((acc, r) => acc + unidades(zone, r), 0);
            reservado = Math.max(reservado, enT);
            if (bloques.length > 0) {
                const reparto = repartoPorBloque(bloques, activas);
                bloques.forEach((b) => {
                    maxPorBloque[b.id] = Math.max(maxPorBloque[b.id] ?? 0, reparto[b.id]);
                });
            }
        });
        return {
            zone,
            tipo: zone.reservable,
            capacidad,
            reservado: Math.min(capacidad, reservado),
            libre: Math.max(0, capacidad - reservado),
            reservas: solapadas,
            bloques: bloques.map((bloque) => {
                const r = Math.min(bloque.puestos, maxPorBloque[bloque.id] ?? 0);
                return {
                    bloque,
                    capacidad: bloque.puestos,
                    reservado: r,
                    libre: bloque.puestos - r,
                    reservas: solapadas.filter((x) => x.bloqueId === bloque.id),
                };
            }),
        };
    },

    /**
     * Disponibilidad en un tramo de varios días: lo peor de todos los días
     * (lo que queda libre todos los días del tramo).
     */
    disponibilidadTramo(
        zoneId: string,
        fecha: string,
        fechaFin: string,
        inicio: number,
        fin: number
    ): Disponibilidad | null {
        return this.disponibilidadDias(zoneId, diasEntre(fecha, fechaFin || fecha), inicio, fin);
    },

    /** Disponibilidad en varios días sueltos: lo que queda libre todos ellos. */
    disponibilidadDias(
        zoneId: string,
        dias: string[],
        inicio: number,
        fin: number
    ): Disponibilidad | null {
        let peor: Disponibilidad | null = null;
        dias.forEach((dia) => {
            const d = this.disponibilidad(zoneId, dia, inicio, fin);
            if (!d) return;
            if (!peor) {
                peor = d;
                return;
            }
            const p: Disponibilidad = peor;
            peor = {
                ...p,
                reservado: Math.max(p.reservado, d.reservado),
                libre: Math.min(p.libre, d.libre),
                reservas: [...p.reservas, ...d.reservas.filter((r) => !p.reservas.includes(r))],
                bloques: p.bloques.map((pb, i) => {
                    const db = d.bloques[i];
                    return {
                        ...pb,
                        reservado: Math.max(pb.reservado, db.reservado),
                        libre: Math.min(pb.libre, db.libre),
                        reservas: [
                            ...pb.reservas,
                            ...db.reservas.filter((r) => !pb.reservas.includes(r)),
                        ],
                    };
                }),
            };
        });
        return peor;
    },

    reservar(params: {
        zoneId: string;
        fecha: string;
        /** Último día incluido; por defecto el mismo día. */
        fechaFin?: string;
        inicio: number;
        fin: number;
        nombre: string;
        cantidad?: number;
        /** Mesa pedida (zonas con bloques); sin ella, la primera con sitio. */
        bloqueId?: string;
        /** "voz": la pide el avatar; su chip sale desplegado en el plano. */
        origen?: "panel" | "voz";
    }): { ok: true; reserva: AforoReserva } | { ok: false; error: string; libre?: number } {
        const zone = zoneById(params.zoneId);
        if (!zone || !zone.reservable) {
            return { ok: false, error: "Ese espacio no se puede reservar" };
        }
        if (!params.nombre.trim()) {
            return { ok: false, error: "Falta el nombre de la persona para la reserva" };
        }
        let { inicio, fin } = params;
        if (zone.reservable === "turno") {
            // Franjas de media hora.
            inicio = Math.floor(inicio / TURNO_HOURS) * TURNO_HOURS;
            fin = Math.max(inicio + TURNO_HOURS, Math.ceil(fin / TURNO_HOURS) * TURNO_HOURS);
        }
        if (!(fin > inicio)) {
            return { ok: false, error: "La hora de fin debe ser posterior a la de inicio" };
        }
        const fechaFin =
            params.fechaFin && params.fechaFin > params.fecha ? params.fechaFin : params.fecha;
        if (diasEntre(params.fecha, fechaFin).length >= MAX_DIAS) {
            return {
                ok: false,
                error: `Una reserva no puede pasar de ${MAX_DIAS - 1} días seguidos`,
            };
        }
        // Salas y despachos se reservan enteros; `cantidad` guarda los
        // asistentes (solo informativo y para la ocupación), sin dato = llena.
        const cantidad =
            zone.reservable === "sala"
                ? Math.min(
                      zone.aforoMaximo,
                      Math.max(1, Math.round(params.cantidad ?? zone.aforoMaximo))
                  )
                : Math.max(1, Math.round(params.cantidad ?? 1));
        const disp = this.disponibilidadTramo(zone.id, params.fecha, fechaFin, inicio, fin);
        if (!disp) {
            return { ok: false, error: "Ese espacio no se puede reservar" };
        }
        const variosDias = fechaFin !== params.fecha;
        const tramoTxt = variosDias ? "en esos días" : "en ese tramo";
        // Una sala ocupa 1 unidad (se reserva entera) aunque vengan varios
        // asistentes; en el resto cada persona es una unidad.
        const necesario = zone.reservable === "sala" ? 1 : cantidad;
        if (disp.libre < necesario) {
            return {
                ok: false,
                error:
                    zone.reservable === "sala"
                        ? `${zone.nombre} ya está reservada ${tramoTxt}`
                        : `Solo quedan ${disp.libre} de ${disp.capacidad} libres ${tramoTxt}`,
                libre: disp.libre,
            };
        }

        // Mesa: la pedida, si tiene sitio; si no, la primera con sitio.
        let bloque: AforoBloque | undefined;
        if (disp.bloques.length > 0) {
            if (params.bloqueId) {
                const pedida = disp.bloques.find((b) => b.bloque.id === params.bloqueId);
                if (!pedida) {
                    return { ok: false, error: "Esa mesa no está en ese espacio" };
                }
                if (pedida.libre < cantidad) {
                    return {
                        ok: false,
                        error: `En ${pedida.bloque.nombre} solo ${
                            pedida.libre === 1 ? "queda" : "quedan"
                        } ${pedida.libre} ${
                            pedida.libre === 1 ? "puesto libre" : "puestos libres"
                        } ${tramoTxt}`,
                        libre: pedida.libre,
                    };
                }
                bloque = pedida.bloque;
            } else {
                const conSitio = disp.bloques.find((b) => b.libre >= cantidad);
                if (!conSitio) {
                    const mayor = disp.bloques.reduce((m, b) => (b.libre > m ? b.libre : m), 0);
                    return {
                        ok: false,
                        error: `No hay ninguna mesa con ${cantidad} puestos juntos ${tramoTxt}; como mucho ${mayor} en una misma mesa`,
                        libre: disp.libre,
                    };
                }
                bloque = conSitio.bloque;
            }
        }

        const reserva: AforoReserva = {
            id: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
            zoneId: zone.id,
            tipo: zone.reservable,
            fecha: params.fecha,
            fechaFin,
            inicio,
            fin,
            nombre: params.nombre.trim(),
            cantidad,
            bloqueId: bloque?.id,
            bloqueNombre: bloque?.nombre,
            creadaEn: new Date().toISOString(),
        };
        reservas = [...reservas, reserva];
        if (params.origen === "voz") destacada = reserva.id;
        save();
        return { ok: true, reserva };
    },

    cancelar(id: string): boolean {
        const before = reservas.length;
        reservas = reservas.filter((r) => r.id !== id);
        if (destacada === id) destacada = null;
        if (reservas.length !== before) {
            save();
            return true;
        }
        return false;
    },

    /** Borra todas las reservas (consola: aforoReservations.borrarTodas()). */
    borrarTodas(): void {
        reservas = [];
        destacada = null;
        save();
    },

    /**
     * Id de la última reserva pedida por voz, para que su chip del plano salga
     * desplegado (nombre y hora) sin tener que pulsarlo; null si no hay.
     */
    destacadaId(): string | null {
        return destacada;
    },

    /** Deja de destacar la reserva por voz (p. ej. al pulsar su chip). */
    quitarDestacada(): void {
        destacada = null;
    },
};

// Para depurar desde la consola del navegador.
(window as unknown as { aforoReservations?: typeof aforoReservations }).aforoReservations =
    aforoReservations;
